import React, { useContext, useState } from 'react';
import './VenueAnalysisBar.scss';
import AppContext from 'app/AppContext';
import classnames from 'classnames';
import { VenueAnalysisVenueDisplayData } from './gridData';
import { VenueAnalysisVenueItem } from './VenueAnalysisVenueItem';
import { getVenueTypeClass } from 'utils/venueTypeUtils';
import {
    isVenueSelected,
    VenueAnalysisSelection,
} from 'widgets/venueAnalysis/store/component/VenueAnalysisSelection';
import {
    HighlightedVenue,
    isVenueItemHighlighted,
    VenueAnalysisBarHighlightMode,
} from 'widgets/venueAnalysis/highlighting';
import { VenueAnalysisTypeToVenueSankey } from 'widgets/venueAnalysis/grid/VenueAnalysisTypeToVenueSankey';
import classNames from 'classnames';
import Tooltip from 'utils/components/Tooltip/Tooltip';

interface VenueAnalysisBarProps {
    width: number;
    className?: string;
    data: VenueAnalysisVenueDisplayData;
    showVenues: boolean;
    showTypeToVenueSankey?: boolean;
    highlightedVenue?: HighlightedVenue;
    onHighlightedVenueChange?: (venue?: HighlightedVenue) => void;
    suppressVenueHighlighting?: boolean;
    highlightOnHover?: boolean;
    highlightMode?: VenueAnalysisBarHighlightMode;
    selection?: VenueAnalysisSelection;
    onSelectionChanged?: (selection?: VenueAnalysisSelection) => void;
    barExpandThresholdPercent?: number;
    barMinDisplayThresholdPercent?: number;
}

const DefaultBarExpandThresholdPercent = 25;
const DefaultBarMinDisplayThresholdPercent = 8;

export function VenueAnalysisBar(props: VenueAnalysisBarProps) {
    const {
        className,
        data,
        showVenues,
        highlightedVenue,
        onHighlightedVenueChange,
        highlightOnHover,
        suppressVenueHighlighting,
        selection,
        onSelectionChanged,
        highlightMode: propsHighlightMode,
        showTypeToVenueSankey,
        width,
        barMinDisplayThresholdPercent: propsBarMinDisplayThresholdPercent,
        barExpandThresholdPercent: propsBarExpandThresholdPercent,
    } = props;

    const barExpandThresholdPercent =
        propsBarExpandThresholdPercent ?? DefaultBarExpandThresholdPercent;
    const barMinDisplayThresholdPercent =
        propsBarMinDisplayThresholdPercent ??
        DefaultBarMinDisplayThresholdPercent;

    const highlightMode: VenueAnalysisBarHighlightMode =
        propsHighlightMode || 'VenueType';
    const { numberFormat } = useContext(AppContext);

    const [hasHover, setHover] = useState(false);

    const isHighlightedByVenueHighlighting =
        !suppressVenueHighlighting &&
        isVenueItemHighlighted(
            highlightMode,
            highlightedVenue,
            selection,
            data.highlightItem
        );
    const isHighlightedByHover = hasHover && highlightOnHover;

    const isHighlighted =
        isHighlightedByHover || isHighlightedByVenueHighlighting;

    const expandOnHover =
        isHighlighted && data.barWidth < barExpandThresholdPercent;

    const isTruncated =
        showVenues &&
        !selection &&
        data.percent < barMinDisplayThresholdPercent;

    const onMouseOver = () => {
        setHover(true);
        onHighlightedVenueChange &&
            onHighlightedVenueChange(data.highlightItem);
    };

    const onMouseLeave = () => {
        setHover(false);
        onHighlightedVenueChange && onHighlightedVenueChange(undefined);
    };

    const barClickable =
        (!selection || isVenueSelected(selection)) && onSelectionChanged;
    const itemClickable = !isVenueSelected(selection) && onSelectionChanged;

    const onVenueTypeAndVenuesClicked = () => {
        if (barClickable) {
            onSelectionChanged!({ venueTypeName: data.name });
        }
    };

    const onVenueClicked = (
        ev: React.MouseEvent<HTMLDivElement>,
        venueName: string
    ) => {
        if (!itemClickable) {
            ev.stopPropagation();
            return;
        }
        onSelectionChanged!({ venueTypeName: data.name, venueName });
    };

    const flexGrowStyle = (width: number, expandOnHover: boolean) => {
        const displayWidth = expandOnHover
            ? barExpandThresholdPercent
            : Math.max(width, 1);

        const maxWidth = displayWidth < 2 ? { maxWidth: '6px' } : {};

        return {
            ...maxWidth,
            flex: `1 1 auto`,
            width: `${displayWidth}%`,
        };
    };

    const barClassName = getVenueTypeClass(
        selection ? selection.venueTypeName : data.venueTypeName ?? data.name
    );

    const nameText = data.name;
    const numberText =
        data.percent < 1
            ? `${numberFormat.n1(data.percent)}%`
            : `${numberFormat.n0(data.percent)}%`;
    const tooltip = `${nameText} ${numberText}`;

    return (
        <div
            className={classnames(
                'VenueAnalysisBar',
                isHighlighted && 'highlight',
                isTruncated && 'truncated',
                barClickable && 'bar-clickable',
                itemClickable && 'item-clickable',
                className
            )}
            style={flexGrowStyle(data.barWidth, expandOnHover)}
            key={data.name}
            onClick={onVenueTypeAndVenuesClicked}
        >
            <Tooltip neverHide={true} text={tooltip} showDelay={200}>
                <div
                    className={classnames('venue-bar', barClassName)}
                    onMouseOver={() => onMouseOver()}
                    onMouseLeave={() => onMouseLeave()}
                >
                    <div className={'text-area'}>
                        <div className="venue-bar-name">{nameText} </div>
                        <div className="number">{numberText}</div>
                    </div>
                </div>
            </Tooltip>
            {showTypeToVenueSankey && isVenueSelected(selection) && (
                <VenueAnalysisTypeToVenueSankey
                    data={data}
                    className={barClassName}
                    selection={selection}
                    width={width}
                />
            )}
            <div
                className={classNames('venue-item-list', barClassName)}
                onMouseOver={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                {data.venues &&
                    showVenues &&
                    data.venues.map((venue) => (
                        <VenueAnalysisVenueItem
                            venue={venue}
                            venueType={data.name}
                            selection={selection}
                            highlightedVenue={highlightedVenue}
                            onHighlightedVenueChange={onHighlightedVenueChange}
                            onClick={(ev) => onVenueClicked(ev, venue.name)}
                            key={venue.name}
                        />
                    ))}
            </div>
        </div>
    );
}
